import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {threadPreviewsQueryKey} from '../page-data/loaders/use-thread-previews'
import {useMarkersActions, usePullRequestBasePath} from '../stores/selectors'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'
import {pullRequestDismissAutomatedReviewCommentPath} from '@github-ui/paths'
import {getCurrentUserLogin} from '@github-ui/client-env'

export function useDismissAutomatedReviewThreadMutation(filePath: string, lineMarkersKey: LineKey | FileKey) {
  const basePath = usePullRequestBasePath()
  const currentUserLogin = getCurrentUserLogin()
  const queryClient = useQueryClient()
  const {updateThread} = useMarkersActions()

  return useMutation({
    mutationFn: async ({
      automatedReviewCommentId,
      reason,
      resolutionNote,
    }: {
      automatedReviewCommentId: string
      reason: string
      resolutionNote?: string
      threadId: string
    }) => {
      const apiUrl = pullRequestDismissAutomatedReviewCommentPath({basePath, automatedReviewCommentId})
      const response = await fetchWithErrorHandling(apiUrl, {
        method: 'PUT',
        body: {
          reason,
          resolution_note: resolutionNote,
        },
      })

      throwErrorsIfBadResponse(response)
    },
    onSuccess: (_data, variables) => {
      updateThread(Number(variables.threadId), filePath, lineMarkersKey, oldThread => {
        if (oldThread?.commentsData?.comments[0]?.automatedComment) {
          const automatedComment = oldThread.commentsData?.comments[0]?.automatedComment
          return {
            ...oldThread,
            isResolved: true,
            resolvedBy: currentUserLogin,
            commentsData: {
              ...oldThread.commentsData,
              comments: [
                {
                  ...oldThread.commentsData.comments[0],
                  automatedComment: {
                    ...automatedComment,
                    isDismissed: true,
                    viewerCanDismiss: false,
                    viewerCanReopen: true,
                  },
                },
                ...oldThread.commentsData.comments.slice(1),
              ],
            },
          }
        }
      })

      // invalidate pullRequestFilesToolbar queries
      queryClient.invalidateQueries({
        queryKey: threadPreviewsQueryKey(basePath),
      })
    },
  })
}
